import { useCallback, useEffect, useMemo, useState } from "react";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { useAuth } from "../context/AuthContext";
import { getCancellationAnalytics, exportCancellationAnalytics } from "../api/reports";
import { listStaff } from "../api/staffs";
import { listCategories } from "../api/services";
import { getCurrentSalonId } from "../utils/currentSalon";
import { getSalonTimezone } from "../utils/salonTimezone";
import { MODAL_TEXT, FIELD_CLASS, LABEL_TEXT, BTN_PRIMARY, BTN_OUTLINE } from "../ui/modalStyles";
import { REPORT_HELP } from "../constants/reportHelpContent";
import "./ReportsSectionCompact.css";

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(value);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function CancellationAnalyticsReport() {
  const { token, user, currentSalon, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.CANCELLATION_ANALYTICS;
  const salonId = getCurrentSalonId(user);
  const baseTimezone = useMemo(
    () => getSalonTimezone(currentSalon) || authSalonTimezone || "UTC",
    [currentSalon, authSalonTimezone]
  );
  const today = useMemo(() => formatDateISO(new Date(), baseTimezone), [baseTimezone]);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [employeeId, setEmployeeId] = useState("");
  const [serviceCategoryId, setServiceCategoryId] = useState("");
  const [bookingSource, setBookingSource] = useState("");
  const [staff, setStaff] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setStartDate(today);
    setEndDate(today);
  }, [today]);

  const loadStaff = useCallback(async () => {
    if (!salonId || !token) return;
    try {
      const data = await listStaff({ salon_id: salonId }, token);
      setStaff(Array.isArray(data) ? data : []);
    } catch (err) {
      setStaff([]);
    }
  }, [salonId, token]);

  const loadCategories = useCallback(async () => {
    if (!salonId || !token) return;
    try {
      const data = await listCategories({ salon_id: salonId }, token);
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setCategories([]);
    }
  }, [salonId, token]);

  useEffect(() => {
    loadStaff();
    loadCategories();
  }, [loadStaff, loadCategories]);

  const isInvalidDateRange = useMemo(() => {
    return startDate && endDate && startDate > endDate;
  }, [startDate, endDate]);

  const handleRunReport = async () => {
    if (isInvalidDateRange) {
      setError("End date cannot be before start date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId ? Number(employeeId) : null,
        service_category_id: serviceCategoryId ? Number(serviceCategoryId) : null,
        booking_source: bookingSource || null,
      };
      const data = await getCancellationAnalytics(payload);
      setReportData(data || null);
    } catch (err) {
      setReportData(null);
      setError("Unable to load cancellation analytics.");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (isInvalidDateRange) {
      setError("End date cannot be before start date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId ? Number(employeeId) : null,
        service_category_id: serviceCategoryId ? Number(serviceCategoryId) : null,
        booking_source: bookingSource || null,
      };
      await exportCancellationAnalytics(payload);
    } catch (err) {
      setError("Unable to export data.");
    } finally {
      setLoading(false);
    }
  };

  const kpis = reportData?.kpis || {};
  const byDay = reportData?.by_day_of_week || [];
  const byTime = reportData?.by_time_of_day || [];
  const byTechnician = reportData?.by_technician || [];
  const topReasons = reportData?.top_reasons || [];
  const activeTimezone = reportData?.meta?.timezone || baseTimezone;

  return (
    <div className={`reportSectionShell min-h-screen bg-slate-100 ${MODAL_TEXT}`}>
      <div className="reportSectionWide report-section-spacious report-section-compact space-y-6">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Cancellations & No-Shows" />
          <div className="px-6 py-6 space-y-6">
            <div
              id="cancellations-overview"
              className={getSectionClassName("cancellations-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_cancellations"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="cancellations-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                "cancellations-filters"
              )}`}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>Start date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>End date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>Technician</label>
                  <select
                    value={employeeId}
                    onChange={(event) => setEmployeeId(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    <option value="">All technicians</option>
                    {staff.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.display_name || person.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>Service category</label>
                  <select
                    value={serviceCategoryId}
                    onChange={(event) => setServiceCategoryId(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    <option value="">All categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>Booking source</label>
                  <select
                    value={bookingSource}
                    onChange={(event) => setBookingSource(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    <option value="">All sources</option>
                    <option value="portal">Client portal</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
              </div>
              <div
                id="cancellations-export"
                className={`flex flex-wrap gap-3 ${getSectionClassName(
                  "cancellations-export"
                )}`}
              >
                <button
                  type="button"
                  onClick={handleRunReport}
                  disabled={loading}
                  className={BTN_PRIMARY}
                >
                  {loading ? "Loading..." : "Run report"}
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={loading}
                  className={BTN_OUTLINE}
                >
                  Export data file
                </button>
                <div className="text-sm text-slate-500">
                  Times shown in {activeTimezone}.
                </div>
              </div>
              {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                  {error}
                </div>
              )}
            </div>

            {reportData && (
              <div className="space-y-6">
                <div
                  id="cancellations-kpis"
                  className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6 ${getSectionClassName(
                    "cancellations-kpis"
                  )}`}
                >
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Total appointments
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.total_appointments || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Cancelled
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.cancelled || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Rescheduled
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.rescheduled || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Late cancel attempts
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.late_cancel_attempts || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Late reschedule attempts
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.late_reschedule_attempts || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Cancellation rate
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.cancellation_rate || 0}%
                    </div>
                  </div>
                </div>

                <div
                  id="cancellations-details"
                  className={`grid grid-cols-1 gap-6 lg:grid-cols-2 ${getSectionClassName(
                    "cancellations-details"
                  )}`}
                >
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      By day of week
                    </h3>
                    <div className="space-y-2 text-sm text-slate-700">
                      {byDay.map((row) => (
                        <div
                          key={row.label}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                        >
                          <span>{row.label}</span>
                          <span className="text-slate-500">
                            {row.cancelled} cancelled · {row.rescheduled} rescheduled ·{" "}
                            {row.late_cancel_attempts} late cancels ·{" "}
                            {row.late_reschedule_attempts} late reschedules
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      By time of day
                    </h3>
                    <div className="space-y-2 text-sm text-slate-700">
                      {byTime.map((row) => (
                        <div
                          key={row.label}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                        >
                          <span>{row.label}</span>
                          <span className="text-slate-500">
                            {row.cancelled} cancelled · {row.rescheduled} rescheduled ·{" "}
                            {row.late_cancel_attempts} late cancels ·{" "}
                            {row.late_reschedule_attempts} late reschedules
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      By technician
                    </h3>
                    <div className="space-y-2 text-sm text-slate-700">
                      {byTechnician.map((row) => (
                        <div
                          key={row.staff_id || row.staff_name}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                        >
                          <span>{row.staff_name}</span>
                          <span className="text-slate-500">
                            {row.cancelled} cancelled · {row.rescheduled} rescheduled ·{" "}
                            {row.late_cancel_attempts} late cancels ·{" "}
                            {row.late_reschedule_attempts} late reschedules
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      Top reasons
                    </h3>
                    <div className="space-y-2 text-sm text-slate-700">
                      {topReasons.length === 0 && (
                        <div className="text-slate-500">No reasons recorded.</div>
                      )}
                      {topReasons.map((row) => (
                        <div
                          key={row.reason}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                        >
                          <span>{row.reason}</span>
                          <span className="text-slate-500">{row.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
